import React from "react";
import LegalShell from "@/components/LegalShell";

export default function PrivacyPolicy() {
  return (
    <LegalShell title="Privacy Policy" subtitle="Effective February 28, 2026 · Version 2.0">
      <Section title="Who we are">
        WarPaint DFS is a subscription analytics platform operated by WarPaint DFS Tech LLC
        ("we", "us", "our"). We are an independent software company and are not affiliated with
        DraftKings, Inc., the NFL, or any other DFS operator.
      </Section>

      <Section title="What we collect from the web app">
        <p className="mb-2">When you use warpaintdfs.com we collect and store:</p>
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li><strong className="text-white">Email address + hashed password</strong> — for login. Passwords are hashed with bcrypt; we never see or store the plaintext.</li>
          <li><strong className="text-white">Stripe customer ID and subscription status</strong> — payment card numbers themselves are held only by Stripe.</li>
          <li><strong className="text-white">DraftKings CSV uploads and generated lineups</strong> — retained in your account for the current NFL season so you can resume mid-slate. Deleted 90 days after the Super Bowl.</li>
          <li><strong className="text-white">Basic request metadata</strong> — IP address (for legality-state check + brute-force protection) and User-Agent string. IPs are hashed after 30 days.</li>
          <li><strong className="text-white">Error telemetry</strong> — via Sentry, when the app crashes we send the stack trace + a scrubbed request context. No passwords or Stripe tokens are sent.</li>
        </ul>
      </Section>

      <Section title="What we do NOT collect">
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li>DraftKings login credentials — we never ask for them and could not use them.</li>
          <li>Payment card numbers or CVCs — Stripe handles these exclusively.</li>
          <li>Social security numbers, date of birth beyond birth-year for age gate, or any other US tax/PII data.</li>
          <li>Contents of your emails, texts, phone contacts, or any data outside the WarPaint DFS app.</li>
          <li>Advertising or third-party marketing pixels.</li>
        </ul>
      </Section>

      <Section title="Cookies & similar technologies">
        We use two kinds of first-party storage:
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li><strong className="text-white">Authentication cookie / JWT</strong> — required to stay logged in. Cannot be disabled without losing access.</li>
          <li><strong className="text-white">UI preference localStorage</strong> — remembers your last-selected slate tab, dark-mode preference, and Turbo Mode toggle. Cleared when you log out.</li>
        </ul>
        <p className="mt-2">We do not use Google Analytics, Facebook Pixel, or any cross-site advertising trackers.</p>
      </Section>

      <Section title="Who we share data with">
        We do not sell your personal data. We share only the minimum necessary information with:
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li><strong className="text-white">Stripe</strong> — email + Stripe customer ID, for billing.</li>
          <li><strong className="text-white">Resend</strong> — email address, for password-reset and transactional emails.</li>
          <li><strong className="text-white">Sentry</strong> — scrubbed stack traces + user_id, for error monitoring.</li>
          <li><strong className="text-white">Anthropic (Claude)</strong> — anonymized slate text + your preset selection, for AI Attack Plan generation. Claude does not retain the data for training under our API contract.</li>
        </ul>
        <p className="mt-3">We never share data with DraftKings, FanDuel, or any other DFS operator.</p>
      </Section>

      <Section title="Your rights (GDPR, CCPA)">
        You may at any time:
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li><strong className="text-white">Export your data</strong> — email us and we'll send you a JSON dump of everything we have on you within 30 days.</li>
          <li><strong className="text-white">Delete your account and data</strong> — click "Delete Account" on your billing page (self-service) or email support. We purge within 7 days; anonymized aggregate stats remain.</li>
          <li><strong className="text-white">Correct inaccurate data</strong> — email us.</li>
          <li><strong className="text-white">Opt out of email</strong> — every email has an unsubscribe link. Transactional emails (password reset, billing receipts) cannot be opted out of while your account is active.</li>
        </ul>
      </Section>

      <Section title="Data retention">
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li>Active accounts: for the life of the account.</li>
          <li>Cancelled subscriptions: 90 days after cancellation, then account marked deletable.</li>
          <li>Deleted accounts: purged within 7 days; Stripe records retained per Stripe's own retention policy.</li>
          <li>Anonymized aggregate stats (e.g., "% of users who ran Turbo Mode last Sunday"): retained indefinitely.</li>
        </ul>
      </Section>

      <Section title="Security">
        We use bcrypt for password hashing, TLS 1.2+ for all API traffic, JWT with a rotating
        signing secret, and rate limiting on authentication endpoints. Payment processing is
        handled exclusively by Stripe (PCI-DSS certified). We do not store card numbers.
      </Section>

      <Section title="Changes to this policy">
        We may update this Privacy Policy. Material changes are emailed to the address on file
        at least 14 days before taking effect. The "Effective" date at the top of this page
        reflects the most recent change.
      </Section>

      <Section title="Contact">
        Privacy questions? Email{" "}
        <a href="mailto:support@warpaintdfs.com" className="text-[#39FF14] underline">
          support@warpaintdfs.com
        </a>
        {" "}with the subject <code className="bg-zinc-900 px-1 text-[#39FF14]">Privacy</code>.
      </Section>
    </LegalShell>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="text-lg font-bold text-white mb-2">{title}</h2>
      <div className="text-sm text-zinc-300 leading-relaxed">{children}</div>
    </div>
  );
}
